import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Form, Input, InputNumber, message, Modal, Radio, Row, Space,
  Switch, Table, Tag, Typography,
} from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import {
  StockItem, LegalEntity, listStockItems, getStockSettings, updateStockSettings,
} from '../api/stock';
import StockItemInfoDrawer from '../components/StockItemInfoDrawer';

const { Title, Text } = Typography;

const StockPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');
  const navigate = useNavigate();

  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState('');
  const [legalEntity, setLegalEntity] = useState<LegalEntity | 'all'>('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeZero, setIncludeZero] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerRefresh, setDrawerRefresh] = useState(0);

  // ── Settings modal
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsMarkup, setSettingsMarkup] = useState<number>(30);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listStockItems({
        page, per_page: perPage,
        search: search || undefined,
        legal_entity: legalEntity === 'all' ? undefined : legalEntity,
        include_inactive: includeInactive,
        include_zero: includeZero,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить склад');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, legalEntity, includeInactive, includeZero]);

  useEffect(() => { load(); }, [load]);

  const openSettings = async () => {
    try {
      const s = await getStockSettings();
      setSettingsMarkup(Number(s.default_markup_pct));
      setSettingsOpen(true);
    } catch (e: any) {
      message.error('Не удалось загрузить настройки');
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await updateStockSettings({ default_markup_pct: settingsMarkup });
      message.success('Настройки сохранены');
      setSettingsOpen(false);
      load();  // обновить selling_price у всех позиций
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    } finally {
      setSettingsSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Юр.лицо', dataIndex: 'legal_entity', key: 'legal_entity', width: 90,
      render: (v: LegalEntity) => (
        <Tag color={v === 'autoregium' ? 'blue' : 'purple'}>
          {v === 'autoregium' ? 'АР' : 'Фомин'}
        </Tag>
      ),
    },
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    {
      title: 'Остаток', dataIndex: 'current_stock', key: 'current_stock', width: 110,
      render: (v: number, r: StockItem) => (
        <span style={{ color: Number(v) <= 0 ? '#999' : undefined }}>
          {Number(v).toLocaleString('ru-RU')} {r.unit}
        </span>
      ),
    },
    {
      title: 'Себестоимость', dataIndex: 'last_receipt_price', key: 'cost', width: 130,
      align: 'right' as const,
      render: (v: number | null) => v != null
        ? <span>{Number(v).toLocaleString('ru-RU')} ₽</span>
        : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Наценка', dataIndex: 'effective_markup_pct', key: 'markup', width: 90,
      align: 'right' as const,
      render: (v: number | null, r: StockItem) => {
        if (v == null) return <span style={{ color: '#999' }}>—</span>;
        const isOverride = r.markup_pct != null;
        return (
          <Text type={isOverride ? undefined : 'secondary'}>
            {Number(v).toLocaleString('ru-RU')}%
            {isOverride ? '' : ''}
          </Text>
        );
      },
    },
    {
      title: 'Продажа', dataIndex: 'selling_price', key: 'sell', width: 130,
      align: 'right' as const,
      render: (v: number | null) => v != null
        ? <b>{Number(v).toLocaleString('ru-RU')} ₽</b>
        : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'ЧЗ', dataIndex: 'is_marked', key: 'is_marked', width: 60,
      render: (v: boolean) => v ? <Tag color="gold">марк.</Tag> : null,
    },
  ], []);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}>Склад — остатки</Title></Col>
        <Col>
          <Space wrap>
            <Radio.Group value={legalEntity} onChange={(e) => { setPage(1); setLegalEntity(e.target.value); }}>
              <Radio.Button value="all">Все</Radio.Button>
              <Radio.Button value="autoregium">АвтоРегиум</Radio.Button>
              <Radio.Button value="fomin">Фомин</Radio.Button>
            </Radio.Group>
            <Input.Search
              placeholder="Поиск"
              allowClear
              onSearch={(val) => { setPage(1); setSearch(val); }}
              style={{ width: 220 }}
            />
            <Space size={4}>
              <span>С нулём</span>
              <Switch checked={includeZero} onChange={(v) => { setPage(1); setIncludeZero(v); }} />
            </Space>
            <Space size={4}>
              <span>Архив</span>
              <Switch checked={includeInactive} onChange={(v) => { setPage(1); setIncludeInactive(v); }} />
            </Space>
            {canWrite && (
              <Button icon={<SettingOutlined />} onClick={openSettings}>Настройки</Button>
            )}
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/stock/receipt/new')}>
                Новый приход
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        onRow={(s) => ({
          onClick: () => { setDrawerId(s.id); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
        rowClassName={(s) => (s.is_active ? '' : 'row-archived')}
      />

      <StockItemInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        itemId={drawerId}
        width={480}
        refreshKey={drawerRefresh}
        onUpdated={() => { setDrawerRefresh(k => k + 1); load(); }}
      />

      <Modal
        title="Настройки склада"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        onOk={saveSettings}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={settingsSaving}
      >
        <Form layout="vertical">
          <Form.Item
            label="Автоматическая наценка по умолчанию, %"
            help="Применяется ко всем товарам без персональной наценки. При изменении обновится цена продажи у всех позиций."
          >
            <InputNumber
              min={0} max={1000} step={1} precision={2}
              value={settingsMarkup}
              onChange={(v) => setSettingsMarkup(Number(v) || 0)}
              style={{ width: 200 }}
              addonAfter="%"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default StockPage;
